import { environment } from 'apps/dstv-via-streaming/src/environments/environment';
import { AnalyticsJourneyKey, AppLoadStep, CommonStep, JourneyEvent } from '../enums/analytics-journey-key';
import { AnalyticsEvent } from '../models/customer/analytics';
import { PrincipalPackage } from '../services/api/package-list';

export type AnalyticsModel = { [key: string]: string | number | boolean };

const getAnalyticsEcomValues = (
  product: PrincipalPackage,
  id: string,
  name: string,
  isContract: boolean,
  quoteAmountOverride?: number
) => {
  const adjustedPrice = product && getProductPrice(product, isContract);
  const priceToUse = quoteAmountOverride || adjustedPrice;

  return {
    currency: 'ZAR',
    value: priceToUse,
    items: [
      {
        item_name: name,
        item_id: id,
        price: priceToUse,
        item_category: 'Stream Only',
        item_category2: isContract ? 'Streaming Contract' : 'Streaming Month to Month',
        item_list_name: 'Benefits of Streaming',
        quantity: 1,
      },
    ],
  };
};

/**
 * @deprecated Price value will be incorrect, as bundle price is not considered
 */
const getSelectAnalyticsEcomValues = (products?: PrincipalPackage[]) => {
  return {
    currency: 'ZAR',
    value: getSelectProductPrice(products),
    items: products?.map((item) => {
      return {
        item_name: item.description,
        item_id: item.code,
        price: getProductPrice(item),
        item_category: 'Stream Only',
        item_list_name: 'Benefits of Streaming',
        quantity: 1,
      };
    }),
  };
};

const getProductPrice = (product: PrincipalPackage, isContract: boolean = false) => {
  if (isContract) {
    return product.contract?.bundlePrice || 0;
  }

  if (product?.includesStream) {
    return product?.price + product?.extraStreamAmount;
  }

  return product?.price;
};

const getSelectProductPrice = (products?: PrincipalPackage[]) => {
  return products?.reduce((totalPrice: number, principalPackage: PrincipalPackage) => {
    return totalPrice + getProductPrice(principalPackage);
  }, 0);
};

export const AnalyticsJourney: any = {
  [AnalyticsJourneyKey.AppLoad]: {
    [CommonStep.StartJourney]: () => ({
      event: 'DStv Stream App Load Start',
      journey: AnalyticsJourneyKey.AppLoad,
      eventCategory: 'load',
      eventAction: null,
    }),
    [AppLoadStep.Authorized]: (info?: AnalyticsModel) => ({
      event: 'Authorize User',
      journey: AnalyticsJourneyKey.AppLoad,
      eventCategory: 'load',
      eventValue: info?.isAuthorized ? 'authorized' : 'unauthorized',
    }),
    [AppLoadStep.Authenticated]: (info?: AnalyticsModel) => ({
      event: 'Authenticate User',
      journey: AnalyticsJourneyKey.AppLoad,
      eventCategory: 'load',
      eventValue: info?.customerNumber,
    }),
    [CommonStep.EndJourney]: (info?: AnalyticsModel) => ({
      event: 'DStv Stream App Loaded',
      journey: AnalyticsJourneyKey.AppLoad,
      eventCategory: 'load',
      pathType: 'internal',
      componentName: info?.componentName,
      sectionName: info?.sectionName,
    }),
    [CommonStep.JourneyFailed]: (info?: AnalyticsModel) => ({
      event: 'DStv Stream App Failed',
      journey: AnalyticsJourneyKey.AppLoad,
      eventCategory: 'error',
      eventValue: info?.error,
      eventArea: info?.event,
    }),
    [JourneyEvent.ViewCart]: (info?: any, user?: any) => ({
      event: JourneyEvent.ViewCart,
      ecommerce: {
        ...getAnalyticsEcomValues(info?.product, info?.selectedPackage, info?.product?.description, info?.isContract),
      },
      ...user,
    }),
    [JourneyEvent.SelectViewCart]: (info?: any, user?: any) => ({
      event: JourneyEvent.ViewCart,
      ecommerce: {
        ...getSelectAnalyticsEcomValues(info?.products),
      },
      ...user,
    }),
    [JourneyEvent.ViewItemList]: (info?: AnalyticsEvent, user?: any) => ({
      event: JourneyEvent.ViewItemList,
      ecommerce: info?.ecommerce,
      ...user,
    }),
    [JourneyEvent.SelectItem]: (info?: AnalyticsEvent, user?: any) => ({
      event: JourneyEvent.SelectItem,
      ecommerce: info?.ecommerce,
      ...user,
    }),
    [JourneyEvent.ViewItem]: (info?: AnalyticsEvent, user?: any) => ({
      event: JourneyEvent.ViewItem,
      ecommerce: info?.ecommerce,
      ...user,
    }),
    [JourneyEvent.AddToCart]: (info?: any, user?: any) => ({
      event: JourneyEvent.AddToCart,
      ecommerce: {
        ...getAnalyticsEcomValues(info?.product, info?.serviceName, info?.productName, info?.isContract),
      },
      ...user,
    }),
    [JourneyEvent.SelectAddToCart]: (info?: any, user?: any) => ({
      event: JourneyEvent.AddToCart,
      ecommerce: {
        ...getSelectAnalyticsEcomValues(info?.products),
      },
      ...user,
    }),
    [JourneyEvent.AddToWishlist]: (info?: AnalyticsEvent, user?: any) => ({
      event: JourneyEvent.AddToWishlist,
      ecommerce: info?.ecommerce,
      ...user,
    }),
    [JourneyEvent.RemoveFromCart]: (info?: any, user?: any) => ({
      event: JourneyEvent.RemoveFromCart,
      ecommerce: {
        ...getAnalyticsEcomValues(info?.product, info?.serviceName, info?.productName, info?.isContract),
      },
      ...user,
    }),
    [JourneyEvent.SelectRemoveFromCart]: (info?: any, user?: any) => ({
      event: JourneyEvent.RemoveFromCart,
      ecommerce: {
        ...getSelectAnalyticsEcomValues(info?.products),
      },
      ...user,
    }),
    [JourneyEvent.BeginCheckout]: (info?: any, user?: any) => ({
      event: JourneyEvent.BeginCheckout,
      ecommerce: {
        ...getAnalyticsEcomValues(
          info?.product,
          info?.selectedPackage,
          info?.product?.description,
          info?.isContract,
          info?.quoteAmountOverride
        ),
      },
      ...user,
    }),
    [JourneyEvent.SelectBeginCheckout]: (info?: any, user?: any) => ({
      event: JourneyEvent.BeginCheckout,
      ecommerce: {
        ...getSelectAnalyticsEcomValues(info?.products),
        value: info?.payableAmount,
      },
      ...user,
    }),
    [JourneyEvent.AddPaymentInfo]: (info?: any, user?: any) => ({
      event: JourneyEvent.AddPaymentInfo,
      ecommerce: {
        payment_type: info?.store?.togglePaymentList
          ? `Once-Off ${info?.store?.selectedPaymentOption.description}`
          : `Recurring ${info?.store?.selectedPaymentOption.description}`,
        ...getAnalyticsEcomValues(
          info?.product,
          info?.selectedPackage,
          info?.product?.description,
          info?.isContract,
          info?.quoteAmountOverride
        ),
      },
      ...user,
    }),
    [JourneyEvent.SelectAddPaymentInfo]: (info?: any, user?: any) => ({
      event: JourneyEvent.AddPaymentInfo,
      ecommerce: {
        payment_type: info?.store?.togglePaymentList
          ? `Once-Off ${info?.store?.selectedPaymentOption.description}`
          : `Recurring ${info?.store?.selectedPaymentOption.description}`,
        ...getSelectAnalyticsEcomValues(info?.products),
        value: info?.payableAmount,
      },
      ...user,
    }),
    [JourneyEvent.Purchase]: (info?: any, user?: any) => ({
      event: JourneyEvent.Purchase,
      ecommerce: {
        transaction_id: info?.transactionId,
        ...getAnalyticsEcomValues(
          info?.product,
          info?.selectedPackage,
          info?.product?.description,
          info?.isContract,
          info?.quoteAmountOverride
        ),
      },
      ...user,
    }),
    [JourneyEvent.SelectPurchase]: (info?: any, user?: any) => ({
      event: JourneyEvent.Purchase,
      ecommerce: {
        transaction_id: info?.transactionId,
        ...getSelectAnalyticsEcomValues(info?.products),
      },
      ...user,
    }),
    [JourneyEvent.PaymentFailure]: (info?: any, user?: any) => ({
      event: JourneyEvent.PaymentFailure,
      ecommerce: {
        error_message: info?.store?.message ? info?.store?.message : 'Unknown',
        transaction_id: info?.store?.transactionId,
        payment_method: info?.store?.paymentMethod,
        ...getAnalyticsEcomValues(
          info?.product,
          info?.selectedPackage,
          info?.product?.description,
          info?.isContract,
          info?.quoteAmountOverride
        ),
      },
      ...user,
    }),
    [JourneyEvent.SelectPaymentFailure]: (info?: any, user?: any) => ({
      event: JourneyEvent.PaymentFailure,
      ecommerce: {
        error_message: info?.store?.message ? info?.store?.message : 'Unknown',
        transaction_id: info?.store?.transactionId,
        payment_method: info?.store?.paymentMethod,
        ...getSelectAnalyticsEcomValues(info?.products),
      },
      ...user,
    }),
    [JourneyEvent.EcomJourney]: (info?: AnalyticsEvent, user?: any) => ({
      event: JourneyEvent.EcomJourney,
      context: info?.context,
      action: info?.action,
      ...user,
    }),
    [JourneyEvent.SelfServiceJourney]: (info?: AnalyticsEvent, user?: any) => ({
      event: JourneyEvent.SelfServiceJourney,
      context: info?.context,
      action: info?.action,
    }),
  },
};
